import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import React, {useLayoutEffect, useRef, useState} from "react";
import Skeleton from "components/redesign/Skeleton";

const useStyles = makeStyles({
  wrap: {
    position: 'relative',
    display: 'block',
    overflow: 'hidden',
  },
  wrapNatural: {
    display: 'inline-block',
    maxWidth: '100%',
  },
  image: {
    display: 'block',
    width: '100%',
      transition: 'opacity 200ms ease',
  },
  fill: {
    height: '100%',
  },
  cover: {
    objectFit: 'cover',
  },
  contain: {
    objectFit: 'contain',
  },
  // The box caps the height and the width follows the image's own ratio. Both
  // are maxima, so a box narrower than that ratio takes height off the image
  // rather than squashing it.
  natural: {
    width: 'auto',
    height: 'auto',
    maxHeight: '100%',
    maxWidth: '100%',
  },
  loading: {
    opacity: 0,
  },
  shimmer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: 'inherit',
  },
});

const ShimmerImage = ({
  src,
  alt = '',
  className,
  imageClassName,
  fit = 'cover',
  placeholderHeight,
  onLoaded,
  style,
  ...rest
}) => {
  const classes = useStyles();
  const imageRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  const settled = useRef(onLoaded);
  settled.current = onLoaded;

  const settle = () => {
    setLoaded(true);
    if (settled.current) settled.current();
  };

  // An image the browser already holds may never fire onLoad, and even when it
  // does the shimmer has already flashed. `complete` is the browser's own
  // answer to "is this one decoded", so a revisited card shows its picture
  // straight away instead of replaying the placeholder — and still reports in,
  // since callers gate on the banner being ready.
  useLayoutEffect(() => {
    if (imageRef.current && imageRef.current.complete) {
      setLoaded(true);
      if (settled.current) settled.current();
    } else {
      setLoaded(false);
    }
  }, [src]);

  return (
    <span
      className={classNames(classes.wrap, fit === 'natural' && classes.wrapNatural, className)}
      style={!loaded && placeholderHeight ? {...style, minHeight: placeholderHeight} : style}
    >
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        onLoad={settle}
        onError={settle}
        className={classNames(
          classes.image,
          fit !== 'auto' && fit !== 'natural' && classes.fill,
          classes[fit],
          !loaded && classes.loading,
          imageClassName
        )}
        {...rest}
      />
      {!loaded && <Skeleton height={'100%'} className={classes.shimmer}/>}
    </span>
  );
};

export default React.memo(ShimmerImage);
